import { useMemo, useState, type ChangeEvent } from 'react';
import { X, Paperclip } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import { useAuth } from '../../context/auth';
import { MAX_ATTACHMENTS } from '../../constants/reports';
import { IssueReportValidationError, useReportStore } from '../../stores/reportStore';
import FullBlockingModal from '../common/FullBlockingModal';
import { Button } from '../common/Button';
import { InputGroup } from '../common/InputGroup';

interface IssueReportModalProps {
  onClose: () => void;
}

export default function IssueReportModal({ onClose }: IssueReportModalProps) {
  const { t } = useTranslation(['reports', 'common']);
  const { user } = useAuth();
  const { submitIssueReport } = useReportStore();

  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [email, setEmail] = useState('');
  const [files, setFiles] = useState<File[]>([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const pageUrl = useMemo(() => window.location.href, []);
  const pagePath = useMemo(() => window.location.pathname, []);
  const hasUserEmail = Boolean(user?.email?.trim());
  const reporterEmail = hasUserEmail ? user!.email!.trim() : email.trim();
  const emailRequired = !hasUserEmail;
  const hasRequiredFields =
    title.trim() !== '' && description.trim() !== '' && (!emailRequired || email.trim() !== '');

  const handleFilesSelected = (event: ChangeEvent<HTMLInputElement>) => {
    const selectedFiles = Array.from(event.target.files || []);
    const uniqueNewFiles = selectedFiles.filter(
      selected =>
        !files.some(existing => existing.name === selected.name && existing.size === selected.size)
    );

    const nextFiles = [...files, ...uniqueNewFiles].slice(0, MAX_ATTACHMENTS);
    setFiles(nextFiles);

    if (event.target) {
      event.target.value = '';
    }
  };

  const removeFile = (fileIndex: number) => {
    setFiles(previous => previous.filter((_, index) => index !== fileIndex));
  };

  const handleSubmit = async () => {
    setError(null);
    setIsSubmitting(true);

    try {
      await submitIssueReport({
        reporter_email: reporterEmail,
        page_url: pageUrl,
        page_path: pagePath,
        issue_title: title,
        issue_description: description,
        files,
      });

      toast.success(t('issue.submit_success', { ns: 'reports' }));
      onClose();
    } catch (submitError) {
      const message =
        submitError instanceof IssueReportValidationError
          ? t(`issue.validation.${submitError.code}`, {
              ns: 'reports',
              ...submitError.params,
            })
          : t('issue.submit_error', { ns: 'reports' });
      setError(message);
      toast.error(message);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <FullBlockingModal innerClassName="md:max-w-[44rem]">
      <div className="flex items-center justify-between gap-3 mb-4">
        <h2 className="text-xl font-semibold text-gray-800">
          {t('issue.modal_title', { ns: 'reports' })}
        </h2>
        <button
          type="button"
          onClick={onClose}
          className="text-gray-500 hover:text-gray-800 transition"
          aria-label={t('common:form_actions.close', { ns: 'common' })}
        >
          <X size={20} />
        </button>
      </div>

      <p className="text-sm text-gray-600 mb-4">
        {t('issue.modal_description', { ns: 'reports' })}
      </p>

      {emailRequired && (
        <div className="mb-4">
          <InputGroup
            label={t('issue.email_label', { ns: 'reports' })}
            value={email}
            onChange={event => setEmail(event.target.value)}
            placeholder={t('issue.email_placeholder', { ns: 'reports' })}
            type="email"
          />
        </div>
      )}

      <div className="mb-4">
        <InputGroup
          label={t('issue.title_label', { ns: 'reports' })}
          value={title}
          onChange={event => setTitle(event.target.value)}
          placeholder={t('issue.title_placeholder', { ns: 'reports' })}
          maxLength={120}
        />
      </div>

      <div className="mb-4">
        <InputGroup
          as="textarea"
          label={t('issue.description_label', { ns: 'reports' })}
          value={description}
          onChange={event => setDescription(event.target.value)}
          placeholder={t('issue.description_placeholder', { ns: 'reports' })}
          maxLength={2000}
        />
      </div>

      <div className="mb-4">
        <InputGroup
          label={t('issue.page_label', { ns: 'reports' })}
          value={pageUrl}
          disabled
          readOnly
        />
      </div>

      {user && (
        <div className="mb-4">
          <p className="font-ambit text-[0.9375rem] leading-[1.25rem] text-secondary mb-2">
            {t('issue.attachments_label', { ns: 'reports' })}
          </p>

          <label className="inline-flex items-center gap-2 rounded-full border border-gray-300 px-4 py-2 text-sm cursor-pointer hover:bg-gray-50 transition">
            <Paperclip size={16} />
            {t('issue.add_screenshot', { ns: 'reports' })}
            <input
              type="file"
              accept="image/png,image/jpeg,image/jpg,image/webp"
              multiple
              className="hidden"
              onChange={handleFilesSelected}
            />
          </label>

          <p className="text-xs text-gray-500 mt-2">
            {t('issue.attachments_hint', {
              ns: 'reports',
              count: files.length,
              max: MAX_ATTACHMENTS,
            })}
          </p>

          {files.length > 0 && (
            <div className="mt-3 flex flex-col gap-2">
              {files.map((file, index) => (
                <div
                  key={`${file.name}-${file.size}`}
                  className="flex items-center justify-between rounded-lg bg-gray-100 px-3 py-2"
                >
                  <p className="text-sm text-gray-700 truncate pr-2">{file.name}</p>
                  <button
                    type="button"
                    onClick={() => removeFile(index)}
                    className="text-sm text-red-600 hover:text-red-700"
                  >
                    {t('issue.remove_attachment', { ns: 'reports' })}
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {error && <p className="text-sm text-red-600 mb-3">{error}</p>}

      <div className="flex justify-end gap-3 mt-6">
        <Button variant="transparent-with-border" onClick={onClose}>
          {t('common:form_actions.cancel', { ns: 'common' })}
        </Button>
        <Button
          variant="primary"
          onClick={handleSubmit}
          disabled={isSubmitting || !hasRequiredFields}
        >
          {isSubmitting
            ? t('common:form_actions.sending', { ns: 'common' })
            : t('issue.submit_button', { ns: 'reports' })}
        </Button>
      </div>
    </FullBlockingModal>
  );
}
